import { cnpj } from 'cpf-cnpj-validator';
import { createMask } from 'imask';
import React, { ChangeEvent, useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FaArrowLeft } from 'react-icons/fa';
import { IValidationResponse, useSteps } from '../../contexts/stepContext';
import { useAppTranslation } from '../../hooks/useAppTranslation';
import api from '../../services/api';
import { Title } from '../../styles/global';
import { errorToast } from '../../utils/toast';
import { Content, GoBack } from '../BodyStep/styles';
import FlowButton from '../FlowButton';
import { FormContent } from '../Identification/styles';
import Input from '../Input';
import Loading from '../Loading';

const maskCnpj = createMask({
  mask: '00.000.000/0000-00',
});

/**
 * Etapa de dados da consulta de Pessoa Jurídica: só o CNPJ.
 *
 * Não há foto, biometria nem data de nascimento, então é esta etapa que
 * dispara a consulta (`POST /validationrequest` com `tipo_pessoa: 'PJ'`) e
 * avança direto para o resultado. O CNPJ fica mascarado na tela e vai só com
 * dígitos para a API — o histórico e a busca por documento comparam dígitos.
 */
const CompanyIdentification: React.FC = () => {
  const [submitting, setSubmitting] = useState(false);

  const {
    documentNum,
    handleSetDocument,
    handleChangeCurrentStep,
    handleReturnCurrentStep,
    currentStep,
    selectedServices,
    setResult,
    setMessageError,
    setServiceError,
    setServiceResults,
  } = useSteps();

  const { handleSubmit } = useForm();
  const { translationPage } = useAppTranslation();
  const { tp } = translationPage(tr => tr.components.companyIdentification);

  const setCnpj = (e: ChangeEvent<HTMLInputElement>) => {
    e.target.value = maskCnpj.resolve(e.target.value);
    handleSetDocument(e.target.value);
  };

  const handleConsult = useCallback(async () => {
    // Trava o reenvio: a consulta é cobrada por execução.
    if (submitting) return;

    // O dígito verificador é conferido aqui antes de gastar a chamada — o
    // backend recusa do mesmo jeito, mas com uma mensagem técnica.
    if (!cnpj.isValid(documentNum)) {
      errorToast({ message: tp('documentInvalid') });
      return;
    }

    setSubmitting(true);
    try {
      const { data } = (await api.post('validationrequest', {
        tipo_pessoa: 'PJ',
        document: documentNum.replace(/\D/g, ''),
        name: null,
        selected_services: selectedServices.map(s => s.cos_ser_id),
      })) as { data: IValidationResponse };

      setResult(data.result ?? '');
      setMessageError(data.message ?? '');
      setServiceError(data.service ?? '');
      setServiceResults(data.results ?? []);

      handleChangeCurrentStep();
    } catch {
      // O interceptor do axios devolve `false` em vez de rejeitar nos erros
      // HTTP (fora o 401) — a desestruturação acima cai aqui também.
      errorToast({ message: tp('requestFailed') });
    } finally {
      setSubmitting(false);
    }
  }, [
    submitting,
    documentNum,
    selectedServices,
    setResult,
    setMessageError,
    setServiceError,
    setServiceResults,
    handleChangeCurrentStep,
    tp,
  ]);

  return (
    <Content>
      {/* Sem seleção de serviços esta é a primeira etapa: não há para onde
          voltar dentro do fluxo. */}
      {currentStep > 1 && (
        <GoBack onClick={handleReturnCurrentStep}>
          <FaArrowLeft size={16} />
          {tp('back')}
        </GoBack>
      )}
      <Title>{tp('title')}</Title>
      <FormContent onSubmit={handleSubmit(handleConsult)}>
        <Input
          type="tel"
          label={tp('documentLabel')}
          onChange={setCnpj}
          value={documentNum}
          placeholder={tp('documentPlaceholder')}
          width="40%"
          autoFocus
        />
        <FlowButton text={tp('next')} type="submit" disabled={submitting} />
      </FormContent>
      {submitting && <Loading />}
    </Content>
  );
};

export default CompanyIdentification;
